/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/* eslint no-console: 0 */

import Translator from './Translator';
import { TranslatorConfig, Translations, LocaleData } from './types';

let singleton: Translator | undefined;
let isConfigured = false;

function configure(config?: TranslatorConfig) {
  singleton = new Translator(config);
  isConfigured = true;

  return singleton;
}

// Custom login functionality - ONLY run on login pages to avoid interfering with other pages
function isLoginPage() {
  if (typeof window === 'undefined') return false;
  const url = window.location.href;
  return url.includes('/login') || url.includes('/auth');
}

function addLoaderToDiv() {
  // Find the target div by its ID
  const container = document.getElementById('loginbox');

  if (container) {
    // Create the loader overlay
    const loaderOverlay = document.createElement('div');
    loaderOverlay.style.position = 'absolute';
    loaderOverlay.style.top = '0';
    loaderOverlay.style.left = '0';
    loaderOverlay.style.width = '100vw';
    loaderOverlay.style.height = '100vh';
    loaderOverlay.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'; // Semi-transparent white background
    loaderOverlay.style.display = 'flex';
    loaderOverlay.style.justifyContent = 'center';
    loaderOverlay.style.alignItems = 'center';
    loaderOverlay.style.zIndex = '1000';

    // Create the loader spinner
    const loader = document.createElement('div');
    loader.style.border = '8px solid #f3f3f3';
    loader.style.borderTop = '8px solid #3498db';
    loader.style.borderRadius = '50%';
    loader.style.width = '50px';
    loader.style.height = '50px';
    loader.style.animation = 'spin 1s linear infinite';

    // Add the spinner to the overlay
    loaderOverlay.appendChild(loader);

    // Add the overlay to the top of the container
    container.prepend(loaderOverlay);

    // Remove the loader after 3 seconds
    setTimeout(() => {
      loaderOverlay.remove();
    }, 3000); // Adjust time as needed

    // Keyframes for the spin animation
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

// Setup login functionality with proper DOM ready check
if (typeof window !== 'undefined') {
  window.onload = function () {
    // Only run login code if we're actually on a login page (check when DOM is ready)
    if (isLoginPage() || document.getElementById('loginbox') !== null) {
      addLoaderToDiv();
      console.log('====Custom Login Handler (Login Page Only)========');
      const url = window.location.href;
      console.log('Login page URL:', url);
      const logoutBtn = document.getElementById('btn-signin-Gain') || document.querySelector('.fa-sign-in');
      if (url.includes('login') && logoutBtn) {
        logoutBtn.click();
        return;
      }
    }
  };
}

function getInstance() {
  if (!isConfigured) {
    console.warn('You should call configure(...) before calling other methods');
  }

  if (typeof singleton === 'undefined') {
    singleton = new Translator();
  }

  return singleton;
}

function resetTranslation() {
  if (isConfigured) {
    isConfigured = false;
    singleton = undefined;
  }
}

function addTranslation(key: string, translations: string[]) {
  return getInstance().addTranslation(key, translations);
}

function addTranslations(translations: Translations) {
  return getInstance().addTranslations(translations);
}

function addLocaleData(data: LocaleData) {
  return getInstance().addLocaleData(data);
}

function t(input: string, ...args: unknown[]) {
  return getInstance().translate(input, ...args);
}

function tn(key: string, ...args: unknown[]) {
  return getInstance().translateWithNumber(key, ...args);
}

export {
  configure,
  addTranslation,
  addTranslations,
  addLocaleData,
  t,
  tn,
  resetTranslation,
};
